import * as _ from 'lodash'
import clientStorage from './clientStorage';
import socketClient from './socketClient';
import util from './util';
import globalRelay from './globalRelay';
import { IPatchOperation } from './modelFactory';

function _get (path, query?, progressCallback?, version?) {
	return _send({ path: path, query: query, version: version }); // eslint-disable-line no-use-before-define
}

function _post (path, data, query?, progressCallback?, version?) {
	return _send({ method: 'POST', path: path, query: query, version: version, data: data }); // eslint-disable-line no-use-before-define
}

function _delete (path, progressCallback?, version?) {
	return _send({ method: 'DELETE', path: path, version: version }); // eslint-disable-line no-use-before-define
}

const defaultPageQuery = {
	pageIndex: 0,
	pageSize: 50
};

const activeRequests:Record<string, Promise<any>> = {};
let authToken;
let authTokenExpires;

// NOTE: Keeps the user logged in while the window is open
const refreshToken = (gateway, timeoutMs) => {
	setTimeout(() => {
		if (authTokenExpires) {
			_get('user/refreshtoken').then(result => {
				gateway.setAuthToken(result.token, true);
				if (authTokenExpires) {
					refreshToken(gateway, timeoutMs);
				}
			});
		}
	}, timeoutMs);
};

const serviceGateway = {
	setAuthToken: function (token?, refreshing?) {
		const tokenUser = (token) ? util.decodeAuthToken(token) : undefined;

		authToken = token;
		authTokenExpires = tokenUser && tokenUser.expires;

		if (authTokenExpires && !refreshing) {
			const now = new Date();
			const msToExpiration = authTokenExpires.getTime() - now.getTime();
			refreshToken(serviceGateway, msToExpiration / 2);
		}

		return clientStorage.setItem('authToken', token)
			.then(() => {
				if (tokenUser) {
					socketClient.setAuthToken(authToken);
					return serviceGateway.fetchCurrentEmployee()
						.then(result => {
							return {
								user: tokenUser,
								employee: result.employee
							};
						})
						.catch(() => {
							return { user: tokenUser, employee: undefined };
						});
				}
				else {
					return { user: undefined, employee: undefined };
				}
			});
	},

	deleteEmployee: function (params) {
		return _delete('employee/' + params.employee.key);
	},

	deleteJobCost: function (params) {
		return _delete('jobcost/' + params.jobCost.key);
	},

	fetchConfig: function (params) {
		return _get(`config/${params.name.toLowerCase()}`);
	},

	fetchContractor: function (params) {
		return _get('contractor/' + params.key);
	},

	fetchCurrentEmployee: function () {
		return _get('employee');
	},

	fetchDomainEntities: function () {
		return Promise.all([_get('systemmodule/list'),
			_get('orgtitle/list'),
			_get('orgdepartment/list'),
			_get('doorsize/list'),
			_get('glasstype/list'),
			_get('subtradetype/list')
		])
			.then(function (results) {
				const orgTitles = {};

				_.forEach(results[1].orgTitles, function (x) {
					orgTitles[x.key] = x;
				});
				const orgDepartments = {};

				_.forEach(results[2].orgDepartments, function (x) {
					orgDepartments[x.key] = x;
				});
				const glassTypes = {};

				_.forEach(results[4].glassTypes, function (x) {
					glassTypes[x.key] = x;
				});
				const doorSizes = {};

				_.forEach(results[3].doorSizes, function (x) {
					doorSizes[x.key] = x;
				});
				const subTradeTypes = {};

				_.forEach(results[5].subTradeTypes, function (x) {
					subTradeTypes[x.key] = x;
				});

				return {
					domainEntities: {
						modules: results[0].systemModules,
						orgTitles: orgTitles,
						orgDepartments: orgDepartments,
						glassTypes: glassTypes,
						doorSizes: doorSizes,
						subTradeTypes: subTradeTypes
					}
				};
			});
	},

	fetchEmployee: function (params) {
		if (params.key) {
			return _get('employee/' + params.key);
		}
		else if (params.userKey) {
			return _get('employee/user/' + params.userKey);
		}
	},

	fetchEmployees: function () {
		return _get('employee/list');
	},

	fetchFilesForJob: function (params) {
		if (params.job.key) {
			return _get('file/list/job/' + params.job.key);
		}
		else if (params.job.accountingId) {
			return _get('file/list/job/accountingId/' + params.job.accountingId);
		}
	},

	fetchFilesForContractor: function (params) {
		if (params.contractor.key) {
			return _get('file/list/contractor/' + params.contractor.key);
		}
	},

	getUploadableFile: function (params) {
		const request = {
			method: 'POST',
			path: 'file/create',
			data: params.file,
			omitFromSignature: ['headers', 'json']
		};
		return _send(request); // eslint-disable-line no-use-before-define
	},

	fetchFile: function (params) {
		return _get('file/' + params.fileKey);
	},

	uploadFile: function (params) {
		const request = {
			method: 'PUT',
			absolutePath: true,
			json: false,
			path: params.url,
			data: params.file,
			progressHandler: params.progressHandler,
			headers: params.headers,
			omitFromSignature: ['headers', 'json']
		};
		return _send(request); // eslint-disable-line no-use-before-define
	},

	deleteFile: function (params) {
		return _delete('file/' + encodeURIComponent(params.file.key));
	},

	fetchFiscalYears: function () {
		return _get('fiscalyear/list');
	},

	deleteJob: function (params) {
		return _delete('job/' + params.job.key);
	},

	fetchJob: function (params) {
		return _get('job/' + params.key);
	},

	fetchJobCost: function (params) {
		return _get('jobcost/' + params.key);
	},

	fetchJobCosts: function (params) {
		if (params.job) {
			return _get('jobcost/list/job/' + params.job.key, defaultPageQuery);
		}
		else if (params.employee) {
			return _get('jobcost/list/employee/' + params.employee.key, defaultPageQuery);
		}
		throw new Error('Invalid fetchJobCosts request.');
	},

	// temporary to update legacy install pay rates en masse
	jobCostUtil: function (params) {
		if (params.installratechange) {
			return _get('jobcost/list/util/installratechange', params.lastId ? { lastId: params.lastId } : undefined);
		}
		throw new Error('Invalid jobCostUtil request.');
	},

	fetchActiveJobs: function (params) {
		const basePath = params.moduleKey? `job/dashboard/${params.moduleKey}`: 'job/list'

		return _get(params.userId ? `${basePath}/${params.userId}` : basePath);
	},

	fetchMessage: function (params) {
		return _get('message/' + params.key);
	},

	fetchMessages: function (params) {
		if (params.channel) {
			return _get('message/channel/' + params.channel);
		}
		throw new Error('Invalid fetchMessages request.');
	},

	deleteMessage: function (params) {
		return _delete('message/' + params.message.key);
	},

	saveMessage: function (params) {
		return _post('message', { viewModel: !params.patch ? params.message : undefined, patch: params.patch, key: !params.patch ? undefined : params.message.key });
	},

	login: function (params) {
		return _post('user/token/' + params.userName, { password: params.password })
			.then(result => {
				return result;
			});
	},

	requestPasswordReset: function (params) {
		return _get('user/resettoken/' + params.email);
	},

	resetPassword: function (params) {
		return _post('user/password/' + params.email + '/' + params.token, { password: params.password });
	},

	saveContractor: function (params) {
		return _post('contractor', { viewModel: !params.patch ? params.contractor : undefined, patch: params.patch, key: !params.patch ? undefined : params.contractor.key });
	},

	deleteContractor: function (params) {
		return _delete('contractor/' + params.contractor.key);
	},

	saveEmployee: function (params) {
		return _post('employee', { viewModel: !params.patch ? params.employee : undefined, patch: params.patch, key: !params.patch ? undefined : params.employee.key });
	},

	sendInvitation: function (params) {
		return _get(`employee/${params.key}/invite`);
	},

	saveJob: function (params) {
		return _post('job', { viewModel: !params.patch ? params.job : undefined, patch: params.patch, key: !params.patch ? undefined : params.job.key });
	},

	saveJobCost: function (params) {
		return _post('jobcost', { viewModel: !params.patch ? params.jobCost : undefined, patch: params.patch, key: !params.patch ? undefined : params.jobCost.key });
	},

	searchContractors: function (params) {
		if (params.filters) {
			return _post('contractor/search', params.filters);
		}
		throw new Error('Invalid searchContractors request.');
	},

	searchJobs: function (params) {
		if (params.client) {
			return _get('job/search/client', params.client);
		}
		else if (params.contact) {
			return _get('job/search/contact', params.contact);
		}
		else if (params.phone) {
			return _get('job/search/phone', { number: encodeURIComponent(params.phone.number) });
		}
		else if (params.jobCostStatuses) {
			return _get('job/search/jobcoststatus', { status: params.jobCostStatuses.join(',') });
		}
		else if (params.relayNumber) {
			return _get('job/search/relaynumber', { relayNumber: params.relayNumber });
		}
		else if (params.accountingId) {
			return _get('job/search/accountingid', { accountingId: params.accountingId });
		}
		else if (params.builderJobNumber) {
			return _get('job/search/builderjobnumber', { builderJobNumber: params.builderJobNumber });
		}
		else if (params.builderName) {
			return _get('job/search/buildername', { builderName: params.builderName });
		}
		else if (params.address) {
			return _get('job/search/address', { street: params.address.street });
		}
		else if (params.builderKey) {
			return _get('job/search/builder', { builderKey: params.builderKey });
		}
		else if (params.retailLeads) {
			return _get('job/search/leads-retail', params.retailLeads);
		}
		else if (params.accountManagerKey) {
			return _get('job/search/account-manager', { employeeKey: params.accountManagerKey });
		}
		else if (params.employeeRole) {
			return _get('job/search/employee', { role: params.employeeRole, name: params.name });
		}
	},

	searchJobStats: function (params) {
		return _get('job/stats', { businessSegment: params.businessSegment, startDate: params.startDate.toISOString(), endDate: params.endDate.toISOString(), employeeKey: params.employeeKey || '' });
	},

	getContracts: function () {
		return _get('jobcost/contracts');
	},

	fetchVendor: function (params) {
		return _get('vendor/' + params.key);
	},

	searchVendors: function (params) {
		if (params.vendor) {
			return _post('vendor/search', params.vendor);
		}
		throw new Error('Invalid searchVendors request.');
	},

	saveVendor: function (params) {
		return _post('vendor', { viewModel: !params.patch ? params.vendor : undefined, patch: params.patch, key: !params.patch ? undefined : params.vendor.key });
	},

	fetchSiteVisit: function (params) {
		return _get('sitevisit/' + params.key);
	},

	fetchSiteVisits: function (params) {
		if (params.job) {
			return _get('sitevisit/list/job/' + params.job.key, defaultPageQuery);
		}
		else if (params.employee) {
			return _get('sitevisit/list/employee/' + params.employee.key, defaultPageQuery);
		}
		throw new Error('Invalid fetchSiteVisits request.');
	},

	saveSiteVisit: function (params) {
		return _post('sitevisit', { viewModel: !params.patch ? params.siteVisit : undefined, patch: params.patch, key: !params.patch ? undefined : params.siteVisit.key });
	},

	submitFieldVisit: function ({key, patch}: {key: string, patch: IPatchOperation[]}) {
		return _post('sitevisit/submit', { patch, key });
	},

	deleteSiteVisit: function (params) {
		return _delete('sitevisit/' + params.siteVisit.key);
	},

	// NOTE: exposed for testing and use by reportActions
	_httpRequest: function (params, callback) {
		const xhr = xhrFactory(); // eslint-disable-line no-use-before-define

		xhr.open(params.method, params.url, true);

		// events
		xhr.addEventListener('load', load); // eslint-disable-line no-use-before-define
		xhr.addEventListener('error', error); // eslint-disable-line no-use-before-define
		xhr.addEventListener('timeout', timeout); // eslint-disable-line no-use-before-define
		if (params.progressHandler) {
			if (['POST', 'PUT'].indexOf(params.method) !== -1) {
				xhr.upload.addEventListener('progress', params.progressHandler);
			}
			else {
				xhr.addEventListener('progress', params.progressHandler);
			}
		}

		// timeout
		if (params.timeout) { xhr.timeout = params.timeout; }

		// headers
		const headers = params.headers || {};

		_.forEach(_.keys(headers), function (k) {
			const value = headers[k];

			if (value) {
				xhr.setRequestHeader(k, value);
			}
		});

		if (params.responseType) { xhr.responseType = params.responseType; }

		// data
		xhr.send(params.data);

		// helper functions //
		function load () {
			const contentType = xhr.getResponseHeader('content-type');

			if (typeof xhr.status === 'undefined' || xhr.status === 200 || xhr.status === 400) {
				const result = (!!contentType && contentType.toLowerCase().indexOf('application/json') !== -1) ? JSON.parse(xhr.responseText) : (xhr.responseType === 'blob') ? xhr.response : xhr.responseText; // eslint-disable-line no-nested-ternary
				callback(null, result, { xhr: xhr, params: params, contentType: contentType });
			}
			else {
				const err = new Error(xhr.statusText);

				(err as any).status = xhr.status;
				callback(err, null, { xhr: xhr, params: params });
			}
		}

		function error (err) {
			const thrown = new Error('XHR Error');

			(thrown as any).sourceError = err;
			callback(thrown, null, { xhr: xhr, params: params });
		}

		function timeout () {
			const err = new Error('XHR Timeout');

			(err as any).isTimeout = true;
			callback(err, null, { xhr: xhr, params: params });
		}
	}
};

function _send (config) {
	const params = _.defaultsDeep({}, config, {
		method: 'GET',
		path: '',
		version: 'v1',
		headers: {},
		query: undefined, // should be an object
		json: true,
		data: undefined
	});

	const isAwsRequest = /amazonaws\.com/.test(params.path);

	params.omitFromSignature = config.omitFromSignature || ['headers', 'data', 'json'];
	params.omitFromSignature.push('omitFromSignature');

	// NOTE: do no pass authorization header to AWS
	if (authToken && !isAwsRequest) {
		params.headers.Authorization = 'Bearer ' + authToken;
	}

	if (!isAwsRequest) {
		params.headers.RelayBroadcastClientKey = socketClient.getUniqueClientKey();
		params.headers.RelayClientMode = globalRelay.clientMode;
	}

	if (params.json) {
		params.data = (params.data) ? JSON.stringify(params.data) : params.data;
		params.headers['Content-Type'] = 'application/json';
	}

	const signature = JSON.stringify(_.omit(params, params.omitFromSignature));
	let promise = activeRequests[signature];

	if (!promise) {
		promise = new Promise(function sendHttp (resolve, reject) {
			params.url = (params.absolutePath) ? params.path : '/api/' + params.version + '/' + params.path;

			if (params.query) {
				params.url += '?' + _.map(params.query, (v, k) => `${k}=${v}`).join('&');
			}

			serviceGateway._httpRequest(params, function (err, res, extra) { // eslint-disable-line no-unused-vars
				activeRequests[signature] = undefined;
				if (!!res && !!res.errors) {
					const msg = _.map(res.errors, e => {
						return e.message;
					}).join('\n');

					reject({
						statusCode: extra?.xhr?.status,
						message: msg,
						serverErrors: res.errors
					});
				}
				else if (err) {
					switch (err.status) {
					case 401:
					case 403: {
						const now = new Date();
						if (window && !authTokenExpires || (authTokenExpires.getTime() - now.getTime()) <= 0) {
							// clear the current, expired auth token
							serviceGateway.setAuthToken();
							// reload the browser to force a new log in
							window.location.href = window.location.href; // eslint-disable-line no-self-assign
						}
						reject({ statusCode: err.status, message: 'Authorization failed.' });
						break;
					}
					default:
						reject({ statusCode: err.status, message: 'Something went wrong.' });
						break;
					}
				}
				else if (params.returnXhr) {
					resolve({ response: res, xhr: extra.xhr });
				}
				else {
					resolve(res);
				}
			});
		});
		activeRequests[signature] = promise;
	}

	return promise;
}

let _xhrFactory;

function xhrFactory () {
	if (_xhrFactory) {
		return _xhrFactory();
	}

	const factories = [
		function () { return new XMLHttpRequest(); }
	];

	for (let i = 0, n = factories.length; i < n; i++) {
		try {
			_xhrFactory = factories[i];
			return _xhrFactory();
		}
		catch (e) {
			// swallow
		}
	}

	throw new Error('XMLHttpRequest is not supported by this browser');
}

export default serviceGateway;